class AnimatedNumber extends HTMLElement {
    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._wrapperElement = document.createElement('div');
        this._textElement = document.createElement('span');
        this._wrapperElement.appendChild(this._textElement);
        this._root.appendChild(this._wrapperElement);
        this.suffix = this.getAttribute('suffix');
        this._prefix = this.getAttribute('prefix'); // Use _prefix as the internal name to avoid clashing with the DOM `prefix` property
        this.popupAnimationEnabled = this.hasAttribute('popupAnimationEnabled');

        this.animateOnShow = this.hasAttribute('animateOnShow');
        this.hasEnteredViewport = false; // Track if the element has entered the viewport
        this.viewportObserver = null; // Initialize observer as null

        this.addStyles();
    }

    connectedCallback() {
        if (this.animateOnShow) {
            this.setupIntersectionObserver();
        }
    }

    disconnectedCallback() {
        // Clean up the observer when the element is removed from the DOM
        if (this.viewportObserver) {
            this.viewportObserver.disconnect();
        }
    }

    setupIntersectionObserver() {
        this.viewportObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !this.hasEnteredViewport) {
                        this.hasEnteredViewport = true;
                        // Trigger initial rendering when the component enters the viewport
                        this.updateNumber(0, this.getAttribute('value') || 0);
                        this.viewportObserver.disconnect(); // No need to keep observing
                    }
                });
            },
            {threshold: 0.1} // Trigger when at least 10% of the element is visible
        );

        this.viewportObserver.observe(this);
    }

    static get observedAttributes() {
        return ['value'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'value') {
            if (oldValue === null && !this.animateOnShow) {
                this.setValue(newValue);
            } else {
                this.updateNumber(Number(oldValue) || 0, Number(newValue) || 0);
            }
        }
    }

    formatNumber(number) {
        return parseInt(number).toLocaleString();
    }

    updateNumber(oldValue, newValue) {
        const increment = newValue - oldValue;

        if (this.popupAnimationEnabled) {
            this.showPopupAnimation(increment);
        }

        // Easing function for a slow finish to the animation
        const easeOut = (t) => {
            return 1 - Math.pow(1 - t, 2);
        };

        const animationDuration = Math.min(1500, increment * 100);
        let startTimestamp = null;

        const step = (currentTimestamp) => {
            if (startTimestamp === null) {
                startTimestamp = currentTimestamp;
            }

            let progress = (currentTimestamp - startTimestamp) / animationDuration;
            progress = Math.min(1, progress); // Clamp progress to ensure it doesn't exceed 1
            const easedProgress = easeOut(progress) || 0;
            this.setValue(oldValue + Math.floor(increment * easedProgress));

            if (progress < 1) {
                window.requestAnimationFrame(step)
            }
        }

        window.requestAnimationFrame(step)
    }

    showPopupAnimation(increment) {
        const existingAnimationsCount = Math.min(
            5,
            this._wrapperElement.querySelectorAll('.plus-anim').length
        );
        const element = document.createElement('span');
        const offsetWidth = this._wrapperElement.offsetWidth;
        element.className = 'plus-anim sm bold';
        element.innerHTML = '+' + this.formatNumber(increment);
        element.style.width = offsetWidth + 'px';
        element.addEventListener('animationend', event => event.target.remove());
        if (existingAnimationsCount > 1) {
            const marginLeft = (Math.floor(Math.random() * offsetWidth * 2) - offsetWidth) * existingAnimationsCount / 10;
            element.style.marginLeft = marginLeft + 'px';
        }

        this._wrapperElement.prepend(element);
    }

    setValue(value) {
        const suffix = this.suffix || '';
        const prefix = this._prefix || '';
        this._textElement.textContent = prefix + this.formatNumber(value) + suffix;
    }

    addStyles() {
        const styles = `
div {
    position: relative;
    display: inline;
}
.plus-anim {
    display: block;
    text-align: center;
    position: absolute;
    font-size: 0.6em;
    color: hsl(120, 100%, 80%);
    opacity: 0;
    animation: add-count 1.5s 1 linear;
    transform: scale(0.5) translateY(-1.25em);
    text-shadow: 0 0.1em 0 hsl(120, 100%, 15%);
}
@keyframes add-count {
    0% {
        transform: scale(0.6) translateY(-1.2em);
        opacity: 0;
    }
    15% {
        opacity: 1;
        transform: scale(1.4) translateY(-0.8em);
    }
    24% {
        transform: scale(0.8)  translateY(-1.10em);
    }
    30% {
        transform: scale(1) translateY(-1em);
        opacity: 1;
    }
    100% {
        opacity: 0;
        transform: scale(1) translateY(-2.4em);
    }
}
        `;
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(styles);

        this._root.adoptedStyleSheets = [sheet];
    }
}

window.customElements.define('animated-number', AnimatedNumber);
